import { Locale } from "@core/schema/Locale.js";
import { AppSyncError } from "@core/utils/aws/appsync/AppSyncError.js";
import { getExpectedError } from "@core/utils/getExpectedError.js";
import type { GlobalSettings } from "../contexts/GlobalSettings.js";

async function doInitSentry(globalSettings: GlobalSettings): Promise<void> {
	const Sentry = await import("@sentry/browser");
	Sentry.init({
		dsn: globalSettings.SENTRY_DSN,
		environment: globalSettings.ENV,
		release: globalSettings.VERSION,
		tracesSampleRate: 1.0,
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration({
				maskAllText: false,
				maskAllInputs: false,
				blockAllMedia: false,
			}),
		],
		beforeSend(event, hint) {
			const exception = hint.originalException;

			// Drop expected / user-facing errors regardless of how they reached Sentry.
			// Be sure to always show a user-friendly error message to the user.
			// (That one is returned by getExpectedError.)
			if (getExpectedError(exception, Locale.cs)) {
				return null;
			}

			if (exception instanceof AppSyncError) {
				event.fingerprint = ["{{ default }}", exception.name, exception.message];
			}

			return event;
		},
	});
}

let sentryInitPromise: Promise<void> | undefined;

export async function initSentry(globalSettings: GlobalSettings): Promise<void> {
	if (!import.meta.env.PROD || !globalSettings.SENTRY_DSN) {
		return;
	}

	await (sentryInitPromise ??= doInitSentry(globalSettings));
}
